import { useState, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { LANGUAGES } from '../../i18n/LanguageContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import { IconBuilding, IconCheck } from '@tabler/icons-react';

const INDUSTRY_TYPES = [
  { value: 'farm',       labelKey: 'set.ind_farm'       },
  { value: 'estate',     labelKey: 'set.ind_estate'     },
  { value: 'contractor', labelKey: 'set.ind_contractor' },
  { value: 'dealership', labelKey: 'set.ind_dealership' },
  { value: 'groundcare', labelKey: 'cat.groundcare'     },
  { value: 'other',      labelKey: 'set.ind_other'      },
];

export default function Organisation() {
  const { t } = useTranslation();
  const { orgId, org, role, refreshProfile } = useAuth();
  const isOrgAdmin = role === 'org_admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [industryType, setIndustryType] = useState('farm');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState('en-GB');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [town, setTown] = useState('');
  const [county, setCounty] = useState('');
  const [postcode, setPostcode] = useState('');
  const [brandColour, setBrandColour] = useState('#E8610A');
  const [orgCode, setOrgCode] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [hseNumber, setHseNumber] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insurancePolicy, setInsurancePolicy] = useState('');
  const [insuranceExpiry, setInsuranceExpiry] = useState('');
  const [holdTheYard, setHoldTheYard] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId));
        if (snap.exists()) {
          const d = snap.data();
          setName(d.name || '');
          setTradingName(d.tradingName || '');
          setCompanyNumber(d.companyNumber || '');
          setVatNumber(d.vatNumber || '');
          setIndustryType(d.industryType || 'farm');
          setPhone(d.phone || '');
          setWebsite(d.website || '');
          setEmail(d.primaryEmail || '');
          setLanguage(d.preferredLanguage || 'en-GB');
          setAddress1(d.address?.line1 || '');
          setAddress2(d.address?.line2 || '');
          setTown(d.address?.town || '');
          setCounty(d.address?.county || '');
          setPostcode(d.address?.postcode || '');
          setBrandColour(d.brandColour || '#E8610A');
          setOrgCode(d.orgCode || '');
          setHseNumber(d.hseNumber || '');
          setInsuranceProvider(d.insuranceProvider || '');
          setInsurancePolicy(d.insurancePolicyNumber || '');
          setInsuranceExpiry(d.insuranceExpiryDate || '');
          // On unless an admin has switched it off.
          setHoldTheYard(d.emailPrefs?.holdTheYard !== false);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId), {
        name: name.trim(),
        tradingName: tradingName.trim() || null,
        companyNumber: companyNumber.trim() || null,
        vatNumber: vatNumber.trim() || null,
        industryType,
        phone: phone.trim() || null,
        website: website.trim() || null,
        primaryEmail: email.trim() || null,
        preferredLanguage: language,
        address: {
          line1: address1.trim(),
          line2: address2.trim() || null,
          town: town.trim(),
          county: county.trim() || null,
          postcode: postcode.trim().toUpperCase(),
          country: 'United Kingdom',
        },
        brandColour,
        hseNumber: hseNumber.trim() || null,
        insuranceProvider: insuranceProvider.trim() || null,
        insurancePolicyNumber: insurancePolicy.trim() || null,
        insuranceExpiryDate: insuranceExpiry || null,
        // Field path, so other email preferences are left alone.
        ...(isOrgAdmin ? { 'emailPrefs.holdTheYard': holdTheYard } : {}),
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      toast.success(t('set.org_saved'));
    } catch (err) {
      console.error(err);
      toast.error(t('set.err_save'));
    }
    setSaving(false);
  };

  const FIELD_STYLE = { width: '100%', boxSizing: 'border-box' };
  const LABEL_STYLE = { display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 };
  const GRID2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 };
  const MB12 = { marginBottom: 12 };

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>{t('common.loading')}</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <IconBuilding size={20} color="var(--color-text-secondary)" stroke={1.5} />
          <h1 style={{ fontSize: 22, fontWeight: 400 }}>{t('set.org_settings')}</h1>
        </div>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('set.identity')}</div>
          <div style={GRID2}>
            <div>
              <label style={LABEL_STYLE}>{t('set.org_name')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} style={FIELD_STYLE} />
            </div>
            <div>
              <label style={LABEL_STYLE}>{t('set.trading_name')}</label>
              <input type="text" placeholder={t('set.if_different')} value={tradingName} onChange={e => setTradingName(e.target.value)} style={FIELD_STYLE} />
            </div>
          </div>
          <div style={GRID2}>
            <div>
              <label style={LABEL_STYLE}>{t('set.company_number')}</label>
              <input type="text" placeholder={t('set.company_number_placeholder')} value={companyNumber} onChange={e => setCompanyNumber(e.target.value)} style={FIELD_STYLE} />
            </div>
            <div>
              <label style={LABEL_STYLE}>{t('set.vat_number')}</label>
              <input type="text" placeholder={t('set.vat_placeholder')} value={vatNumber} onChange={e => setVatNumber(e.target.value)} style={FIELD_STYLE} />
            </div>
          </div>
          <div style={MB12}>
            <label style={LABEL_STYLE}>{t('set.industry_type')}</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {INDUSTRY_TYPES.map(ind => (
                <div
                  key={ind.value}
                  onClick={() => setIndustryType(ind.value)}
                  style={{
                    padding: '5px 12px', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer',
                    border: '0.5px solid',
                    borderColor: industryType === ind.value ? 'var(--color-brand)' : 'var(--color-border-medium)',
                    background: industryType === ind.value ? 'var(--color-brand-light)' : 'transparent',
                    color: industryType === ind.value ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                    transition: 'all 0.15s',
                  }}
                >
                  {t(ind.labelKey)}
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('staff.contact')}</div>
          <div style={GRID2}>
            <div>
              <label style={LABEL_STYLE}>{t('set.primary_email')}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={FIELD_STYLE} />
            </div>
            <div>
              <label style={LABEL_STYLE}>{t('set.phone_number')}</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} style={FIELD_STYLE} />
            </div>
            <div>
              <label style={LABEL_STYLE}>{t('set.language')}</label>
              <select value={language} onChange={e => setLanguage(e.target.value)} style={FIELD_STYLE}>
                {LANGUAGES.map(l => (<option key={l.code} value={l.code}>{l.label}</option>))}
              </select>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {t('set.language_hint')}
              </div>
            </div>
          </div>
          <div style={MB12}>
            <label style={LABEL_STYLE}>{t('set.website')}</label>
            <input type="text" placeholder={t('set.website_placeholder')} value={website} onChange={e => setWebsite(e.target.value)} style={FIELD_STYLE} />
          </div>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('set.address')}</div>
          <div style={MB12}>
            <label style={LABEL_STYLE}>{t('set.address1')}</label>
            <input type="text" value={address1} onChange={e => setAddress1(e.target.value)} style={FIELD_STYLE} />
          </div>
          <div style={MB12}>
            <label style={LABEL_STYLE}>{t('set.address2')}</label>
            <input type="text" value={address2} onChange={e => setAddress2(e.target.value)} style={FIELD_STYLE} />
          </div>
          <div style={GRID2}>
            <div>
              <label style={LABEL_STYLE}>{t('set.town')}</label>
              <input type="text" value={town} onChange={e => setTown(e.target.value)} style={FIELD_STYLE} />
            </div>
            <div>
              <label style={LABEL_STYLE}>{t('set.county')}</label>
              <input type="text" value={county} onChange={e => setCounty(e.target.value)} style={FIELD_STYLE} />
            </div>
          </div>
          <div style={{ maxWidth: 200 }}>
            <label style={LABEL_STYLE}>{t('set.postcode')}</label>
            <input type="text" value={postcode} onChange={e => setPostcode(e.target.value.toUpperCase())} style={FIELD_STYLE} />
          </div>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('set.branding')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <label style={LABEL_STYLE}>{t('set.brand_colour')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="color"
                  value={brandColour}
                  onChange={e => setBrandColour(e.target.value)}
                  style={{ width: 48, height: 36, padding: 2, border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={brandColour}
                  onChange={e => setBrandColour(e.target.value)}
                  style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 13 }}
                />
              </div>
            </div>
            <div style={{ width: 60, height: 60, borderRadius: 'var(--radius-lg)', background: brandColour, flexShrink: 0 }} />
          </div>
        </Card>

        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('set.compliance')}</div>
          <div style={GRID2}>
            <div>
              <label style={LABEL_STYLE}>{t('set.hse_number')}</label>
              <input type="text" value={hseNumber} onChange={e => setHseNumber(e.target.value)} style={FIELD_STYLE} />
            </div>
            <div>
              <label style={LABEL_STYLE}>{t('set.insurance_provider')}</label>
              <input type="text" value={insuranceProvider} onChange={e => setInsuranceProvider(e.target.value)} style={FIELD_STYLE} />
            </div>
          </div>
          <div style={GRID2}>
            <div>
              <label style={LABEL_STYLE}>{t('set.policy_number')}</label>
              <input type="text" value={insurancePolicy} onChange={e => setInsurancePolicy(e.target.value)} style={FIELD_STYLE} />
            </div>
            <div>
              <label style={LABEL_STYLE}>{t('set.insurance_expiry')}</label>
              <input type="date" value={insuranceExpiry} onChange={e => setInsuranceExpiry(e.target.value)} style={FIELD_STYLE} />
            </div>
          </div>
        </Card>

        {isOrgAdmin && (
          <Card style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('set.emails')}</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13 }}>{t('set.hold_the_yard')}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{t('set.hold_the_yard_desc')}</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={holdTheYard}
                aria-label={t('set.hold_the_yard')}
                onClick={() => setHoldTheYard(v => !v)}
                style={{
                  width: 42,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  borderRadius: 12,
                  background: holdTheYard ? 'var(--color-brand)' : 'var(--color-bg-tertiary)',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}
              >
                <span style={{
                  display: 'block',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: 3,
                  left: holdTheYard ? 21 : 3,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }} />
              </button>
            </div>
          </Card>
        )}

        <Card>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('set.sharing_transfers')}</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            {t('set.org_code_desc')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{t('set.org_code')}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 500, letterSpacing: '0.05em' }}>
                {orgCode || t('set.generating')}
              </div>
            </div>
            {orgCode && (
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(orgCode);
                  setCodeCopied(true);
                  setTimeout(() => setCodeCopied(false), 2000);
                }}
                style={{
                  padding: '8px 14px',
                  background: 'var(--color-bg-primary)',
                  border: '0.5px solid var(--color-border-light)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {codeCopied ? t('set.copied') : t('set.copy')}
              </button>
            )}
          </div>
        </Card>

        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving}
          loading={saving}
          icon={<IconCheck size={14} stroke={1.5} />}
        >
          {saving ? t('common.saving') : t('set.save_changes')}
        </Button>
      </div>
    </Layout>
  );
}